import React from 'react';
import SimpleReactLightbox, {
  SRLWrapper,
  useLightbox,
} from 'simple-react-lightbox';
import { WPBlockProps } from '../wp-blocks/types';

export interface HighlightBlockAttributes {
  lazyblock: string;
  title: string;
  blockId: string;
  // JSON data
  highlights: string;
}

type ImageProp = {
  url: string;
  alt: string;
  id: number;
  caption?: number;
};

const options = {
  settings: {
    disablePanzoom: false,
  },
  thumbnails: {
    showThumbnails: true,
  },
};

export interface ProjectHighlightsBlock {
  blockId: string;
  groupTitle: string;
  highlights: {
    image: ImageProp;
    images: ImageProp[];
    description: string;
  }[];
}

interface HighlightProps {
  highlight: {
    image: ImageProp;
    images: ImageProp[];
    description: string;
  };
}

const parseProjectHighlightBlock = (
  block,
): ProjectHighlightsBlock | undefined => {
  if (!block) {
    return undefined;
  }
  if (block.__typename === 'WpLazyblockProjectHighlightBlock') {
    const jsonHighlights = block.attributes.highlights
      ? JSON.parse(decodeURIComponent(block.attributes.highlights))
      : [];
    return {
      blockId: block.attributes.blockId,
      groupTitle: block.attributes.title,
      highlights: jsonHighlights,
    };
  }
  return undefined;
};

export default function ProjectHighlights({
  block,
}: WPBlockProps<HighlightBlockAttributes>): JSX.Element | null {
  const highlightGroup = parseProjectHighlightBlock(block);
  if (!highlightGroup) {
    return null;
  }

  const groupTitle = highlightGroup?.groupTitle ?? '';
  const highlights = highlightGroup?.highlights;

  return (
    <section key={highlightGroup.blockId} className="project-highlight__group">
      <div className="project-highlight__groupTitleContainer">
        <h3 className="project-highlight__groupTitle">{groupTitle}</h3>
      </div>
      <div className="project-highlight__container">
        {highlights &&
          highlights.map((highlight, index) => (
            <SimpleReactLightbox key={`lighbox-${index}`}>
              <ProjektHighlight key={index} highlight={highlight} />
            </SimpleReactLightbox>
          ))}
      </div>
    </section>
  );
}

function ProjektHighlight({ highlight }: HighlightProps): JSX.Element {
  const { openLightbox } = useLightbox();
  return (
    <div className="project-highlight" onClick={() => openLightbox()}>
      {highlight.image && (
        <div className="project-highlight__imageContainer">
          <img src={highlight.image.url} alt={highlight.image.alt} />
        </div>
      )}
      <SRLWrapper
        options={options}
        images={highlight.images.map((image) => ({
          src: image.url,
          caption: image.caption,
        }))}
      />
      <div className="project-highlight__desc">{highlight.description}</div>
    </div>
  );
}
